<template>
  <main class="about">
    <section class="about__section about__section--intro">
      <div class="about__section-wrap">
        <h1 class="about__section-title">О Tripster</h1>
        <div class="about__section-text">
          Мы&nbsp;хотим, чтобы в&nbsp;любой точке мира вы&nbsp;могли встретиться с&nbsp;близкими по&nbsp;духу людьми,
          которые откроют для вас все самое увлекательное, ценное и&nbsp;неожиданное, что есть в&nbsp;новом городе.
        </div>
        <div class="about__section-text">
          С&nbsp;2013 года мы&nbsp;находим лучших гидов и&nbsp;проводим отличные
          <a href="/">индивидуальные и&nbsp;групповые экскурсии&nbsp;в {{ citiesCountText }} мира</a>. О&nbsp;том,
          насколько хорошо у&nbsp;нас это получается, лучше всего расскажут отзывы наших путешественников: на&nbsp;сайте
          их&nbsp;более&nbsp;{{ reviewsCountText }}.
        </div>

        <div class="about__section-statistics" :aria-busy="isPending">
          <div v-for="item in statistics" :key="item.text" class="about__section-statistics-item">
            <h3 class="about__section-statistics-title">{{ item.value }}</h3>
            {{ item.text }}
          </div>
        </div>
      </div>
    </section>

    <section class="about__section about__section--advantages">
      <div class="about__section-wrap">
        <div class="about__section-advantages">
          <div v-for="item in ADVANTAGES" :key="item.title" class="about__section-advantages-item">
            <h3 class="about__section-advantages-title">{{ item.title }}</h3>
            {{ item.text }}
          </div>
        </div>
      </div>
    </section>

    <section class="about__section about__section--traveler-love">
      <div class="about__section-wrap">
        <h3 class="about__section-title">За&nbsp;что нас любят путешественники</h3>
        <div class="about__section-text">
          Мы&nbsp;знаем, как сделать экскурсии классными и&nbsp;качественными. С&nbsp;каждым новым гидом
          мы&nbsp;беседуем лично и&nbsp;вместе разрабатываем экскурсионную программу. В&nbsp;результате экскурсии
          получаются насыщенными, интересными, структурированными, нравятся нам самим и&nbsp;нашим путешественникам.
        </div>
      </div>
    </section>

    <section class="about__section about__section--how">
      <div class="about__section-wrap">
        <h3 class="about__section-title">Как мы работаем</h3>
        <div v-for="item in HOW_IT_WORKS" :key="item.title" class="about__section-step">
          <img class="about__section-step-image" :src="item.image" alt="" loading="lazy" />
          <div>
            <div class="about__section-step-title">{{ item.title }}</div>
            <div class="about__section-step-text">{{ item.text }}</div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="faq.length" class="about__section about__section--faq">
      <div class="about__section-wrap" :aria-busy="isPending">
        <h3 class="about__section-title">Частые вопросы</h3>
        <div v-for="question in faq" :key="question.id" class="about__section-faq-item">
          <div class="about__section-faq-title">{{ question.question }}</div>
          <span v-html="question.answer" />
        </div>
        <div class="about__section-faq-more">
          <UiButton
            data-test="about-faq-button-more"
            href="/help-center/travelers/"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            text="Показать больше вопросов"
          />
        </div>
        <div class="about__section-faq-contacts">
          У&nbsp;вас остались вопросы? Напишите нам на&nbsp;адрес
          <a href="mailto:support@tripster.ru">support@tripster.ru</a><br />
          или позвоните по телефону <a href="tel:+74951463966">+7&nbsp;(495)&nbsp;146-39-66</a>.
        </div>
      </div>
    </section>

    <section v-if="reviews.length" class="about__section about__section--reviews">
      <div class="about__section-wrap">
        <h3 class="about__section-title">Отзывы путешественников</h3>

        <ReviewsContainer :reviews-data="reviews" />
      </div>
    </section>

    <SubscriptionBlock :api-url="VITE_API_URL" :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL" page-type="listing" />
  </main>
</template>

<script setup lang="ts">
import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
import { formatTripsterNumber } from '@experience/money-utils';
import { ReviewsContainer } from '@experience/reviews-container';
import { choosePlural } from '@experience/shared';
import { SubscriptionBlock } from '@experience/subscription-block-package';
import { useSeoMeta } from '@unhead/vue';
import { computed } from 'vue';

import { useAboutLandingQuery } from '@/entities/about/model/useAboutLandingQuery';
import { ADVANTAGES, HOW_IT_WORKS } from '@/pages/about/model/constants';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { useRenderReport } from '@/shared/lib/lifecycle';

const VITE_API_URL = import.meta.env.VITE_API_URL;
const { VITE_RUNTIME_SOGU_SERVICE_URL } = getRuntimeConfig();
const { data, isPending } = useAboutLandingQuery();

useRenderReport();
useSeoMeta({
  title: 'О проекте — Tripster',
  description:
    'Мы хотим, чтобы в любой точке мира вы могли встретиться с близкими по духу людьми, которые откроют для вас все самое увлекательное, ценное и неожиданное, что есть в новом городе. С 2013 года мы находим лучших гидов и проводим отличные индивидуальные и групповые экскурсии в 1036 городах мира',
});

const citiesCount = computed(() => data.value?.statistics.cities.count);
const reviewsCount = computed(() => data.value?.statistics.reviews.count);
const travelersCount = computed(() => data.value?.statistics.travelers.last_year);
const currentYear = String(new Date().getFullYear() - 1);

const citiesCountText = computed(() => {
  if (!citiesCount.value) {
    return '—';
  }

  return `${formatTripsterNumber(citiesCount.value)} ${choosePlural(citiesCount.value, ['городе', 'городах', 'городах'])}`;
});

const reviewsCountText = computed(() => {
  if (!reviewsCount.value) {
    return '';
  }

  return formatTripsterNumber(Math.floor(reviewsCount.value / 100_000) * 100_000);
});
const faq = computed(() => data.value?.faq.main_questions ?? []);
const reviews = computed(() => data.value?.reviews ?? []);
const statistics = computed(() => [
  {
    value: citiesCount.value ? formatTripsterNumber(citiesCount.value) : '—',
    text: citiesCount.value
      ? `${choosePlural(citiesCount.value, ['город', 'города', 'городов'])} мира`
      : 'городов мира',
  },
  {
    value: travelersCount.value ? formatTripsterNumber(travelersCount.value) : '—',
    text: `человек сходили на наши экскурсии в ${currentYear} году`,
  },
  {
    value: reviewsCount.value ? formatTripsterNumber(reviewsCount.value) : '—',
    text: !reviewsCount.value
      ? 'отзывов путешественников'
      : choosePlural(reviewsCount.value, [
          'отзыв путешественника',
          'отзыва путешественника',
          'отзывов путешественников',
        ]),
  },
]);
</script>

<style scoped lang="scss">
.about {
  color: var(--alias-color-text-default);

  &__section {
    padding: var(--alias-space-40) 0;
    background: var(--alias-color-background-default);

    &--intro,
    &--traveler-love,
    &--faq {
      background: var(--alias-color-background-subtle);
    }

    &--how {
      padding-bottom: 60px;
    }

    &--reviews {
      overflow: hidden;

      .about__section-title {
        margin: 0 0 var(--alias-space-20);
      }
    }

    &--faq {
      position: relative;
      padding: 45px 0;

      &::before {
        position: absolute;
        top: -24px;
        left: 50%;
        width: 48px;
        height: 48px;
        margin-left: -24px;
        content: '';
      }

      .about__section-title {
        margin-bottom: 35px;
      }
    }

    &-wrap {
      min-width: 950px;
      max-width: 1220px;
      padding: 0 var(--alias-space-20);
      margin: 0 auto;

      @include adaptivity.tablet {
        box-sizing: border-box;
        width: 100%;
        min-width: 100%;
        padding: 0 var(--alias-space-20);
      }
    }

    &-title {
      font-size: 31px;
      font-weight: 800;
      text-align: center;

      @include adaptivity.mobile {
        font-size: 28px;
      }
    }

    &-text {
      margin: 10px var(--alias-space-120) 0;
      font-size: 22px;
      font-weight: var(--font-weight-regular);
      line-height: 1.3em;

      & + & {
        margin-top: var(--alias-space-20);
      }

      a {
        color: var(--alias-color-link-default);
        text-decoration: none;

        &:hover {
          color: var(--alias-color-link-default-hovered);
        }
      }

      @include adaptivity.mobile {
        margin-right: 0;
        margin-left: 0;
      }
    }

    &-statistics {
      display: flex;
      gap: 0 2%;
      justify-content: center;
      padding: 30px 0 0;
      text-align: center;

      @include adaptivity.mobile {
        flex-direction: column;
        gap: 10px 0;
      }
    }

    &-statistics-item {
      box-sizing: border-box;
      width: 20%;
      padding: var(--alias-space-20);
      font-size: var(--font-size-400);
      text-align: center;
      text-decoration: none;
      border: 3px solid var(--alias-color-border-accent-green);
      border-radius: 5px;

      @include adaptivity.tablet {
        padding: var(--alias-space-20) 10px;
        font-size: 14px;
      }

      @include adaptivity.mobile {
        width: 100%;
        font-size: var(--font-size-500);
      }
    }

    &-statistics-title {
      margin: 0 0 10px;
      font-size: 30px;
      font-weight: 700;
    }

    &-advantages {
      display: flex;
      justify-content: space-between;
      margin: 0 var(--alias-space-120);

      @include adaptivity.mobile {
        flex-direction: column;
        gap: 30px 0;
        margin: 0;
      }
    }

    &-advantages-item {
      width: 30%;
      padding-top: 10px;
      font-size: var(--font-size-400);
      line-height: 1.4em;

      @include adaptivity.mobile {
        width: 100%;
      }
    }

    &-advantages-title {
      margin: 17px 0 10px;
      font-size: 22px;
      font-weight: 600;
      text-align: left;
    }

    &-step {
      display: flex;
      gap: 0 var(--alias-space-40);
      align-items: center;
      margin: var(--alias-space-40) 180px 0;

      &:first-child {
        margin: 0;
      }

      @include adaptivity.mobile {
        margin: 50px 0 0;
      }
    }

    &-step-image {
      width: 140px;

      @include adaptivity.mobile {
        display: none;
      }
    }

    &-step-text {
      font-size: var(--font-size-500);
      line-height: 1.3em;
    }

    &-step-title {
      margin-bottom: var(--alias-space-12);
      font-size: 22px;
      font-weight: 600;
      color: var(--alias-color-text-default);
    }

    &-faq-item {
      padding: var(--alias-space-20) 0;
      margin: 0 var(--alias-space-120);
      font-size: 17px;
      line-height: 1.4em;
      color: var(--alias-color-text-default);
      border-top: 1px solid var(--alias-color-border-default);

      &:nth-of-type(1) {
        border: 0;
      }

      :deep(a) {
        color: var(--alias-color-link-default);
        text-decoration: none;
      }

      :deep(ul) {
        padding: 0;
        margin: 0;
        list-style: none;
      }

      @include adaptivity.mobile {
        margin: 0;
      }
    }

    &-faq-title {
      margin-bottom: 6px;
      font-size: var(--font-size-500);
      font-weight: 600;
    }

    &-faq-item :deep(p) {
      margin: 0 0 1em;
    }

    &-faq-contacts {
      margin-top: var(--alias-space-20);
      font-size: var(--font-size-600);
      line-height: 1.4em;
      text-align: center;

      a {
        font-weight: 600;
        color: var(--alias-color-link-default);
        text-decoration: none;
      }
    }

    &-faq-more {
      display: flex;
      justify-content: center;
      margin-top: 25px;
    }
  }
}
</style>
